import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import type { TAnalyticsMyPlacementNodeBE, TAnalyticsMyRequirementBE, TAnalyticsMyRuleBE } from '@onesource/schemas';
import { Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { type FC, type MouseEvent, useMemo, useState } from 'react';
import { MdExpandMore } from 'react-icons/md';

import { RequirementRow } from './requirement-row';

/** A lesson or sub-lesson bucket. Sub-lessons never nest further — the content
 *  model caps the hierarchy at two lesson levels. */
interface LessonGroup {
  id: string;
  name: string;
  href: string | null;
  /** Requirements on the lesson itself, above any of its sub-lessons. */
  requirements: TAnalyticsMyRequirementBE[];
  subLessons: LessonGroup[];
}

interface ModuleGroup {
  id: string;
  name: string | null;
  href: string | null;
  /** Requirements that sit under no lesson at all (e.g. a space-level form). */
  requirements: TAnalyticsMyRequirementBE[];
  lessons: LessonGroup[];
}

const PROGRAM_GROUP_ID = '__program__';

const lessonRequirements = (lesson: LessonGroup): TAnalyticsMyRequirementBE[] => [
  ...lesson.requirements,
  ...lesson.subLessons.flatMap(sub => sub.requirements),
];

const moduleRequirements = (group: ModuleGroup): TAnalyticsMyRequirementBE[] => [
  ...group.requirements,
  ...group.lessons.flatMap(lessonRequirements),
];

/**
 * Group a rule's requirements the way the content tree reads: module
 * (sub-space) → lesson → sub-lesson. Requirements with no module (content
 * directly under the program, referenced rules) trail in a final unnamed group,
 * and within a module anything with no lesson is listed before the lessons.
 *
 * `requirement.lesson` is always the lesson that *directly* owns the content, so
 * for a sub-lesson it is the sub-lesson itself and `parentLesson` names the one
 * above it. Buckets are created on first sight, which keeps everything in leaf
 * order (L0…Ln) rather than imposing an order of its own.
 */
const buildGroups = (rule: TAnalyticsMyRuleBE, programSlug: string): ModuleGroup[] => {
  const moduleHref = (slug: string | null): string | null =>
    slug ? `/programs/${programSlug}/${slug.toLowerCase().replace(/_/g, '-')}` : null;

  const groups = new Map<string, ModuleGroup>();
  rule.modules.forEach(module =>
    groups.set(module.id, {
      id: module.id,
      name: module.name,
      href: moduleHref(module.slug),
      requirements: [],
      lessons: [],
    }),
  );
  const rest: ModuleGroup = { id: PROGRAM_GROUP_ID, name: null, href: null, requirements: [], lessons: [] };

  // A lesson slug resolves at the same URL depth whether or not it is a
  // sub-lesson, so both levels build their link the same way.
  const lessonHref = (group: ModuleGroup, node: TAnalyticsMyPlacementNodeBE): string | null =>
    group.href && node.slug ? `${group.href}/${node.slug}` : null;

  const bucketFor = (group: ModuleGroup, node: TAnalyticsMyPlacementNodeBE, into: LessonGroup[]): LessonGroup => {
    const existing = into.find(l => l.id === node.id);
    if (existing) {
      return existing;
    }
    const created: LessonGroup = {
      id: node.id,
      name: node.name,
      href: lessonHref(group, node),
      requirements: [],
      subLessons: [],
    };
    into.push(created);
    return created;
  };

  rule.requirements.forEach(req => {
    const group = (req.module ? groups.get(req.module.id) : undefined) ?? rest;
    if (!req.lesson) {
      group.requirements.push(req);
      return;
    }
    if (req.parentLesson) {
      const parent = bucketFor(group, req.parentLesson, group.lessons);
      bucketFor(group, req.lesson, parent.subLessons).requirements.push(req);
      return;
    }
    bucketFor(group, req.lesson, group.lessons).requirements.push(req);
  });

  const ordered = [...groups.values()].filter(g => moduleRequirements(g).length > 0);
  return moduleRequirements(rest).length > 0 ? [...ordered, rest] : ordered;
};

/** Chevron that turns to point up while its section is open. */
const ExpandChevron: FC<{ open: boolean; size?: number }> = ({ open, size = 22 }) => (
  <MdExpandMore
    size={size}
    style={{ flexShrink: 0, transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }}
  />
);

/** A name that opens its section's content, without also toggling the section. */
const SectionLabel: FC<{ name: string; href: string | null }> = ({ name, href }) => {
  const label = <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(name) }} />;
  return href ? (
    <Link
      to={href}
      style={{ color: 'inherit', textDecoration: 'none' }}
      onClick={(e: MouseEvent) => e.stopPropagation()}
    >
      {label}
    </Link>
  ) : (
    label
  );
};

/**
 * One analytics rule: progress bar, "all of / any of" hint and its requirements
 * grouped by module, lesson and sub-lesson. Every section collapses
 * independently; everything starts expanded.
 */
export const RuleCard: FC<{ rule: TAnalyticsMyRuleBE; programSlug: string }> = ({ rule, programSlug }) => {
  const groups = useMemo(() => buildGroups(rule, programSlug), [rule, programSlug]);
  const showGroupHeaders = groups.length > 1 || (groups.length === 1 && groups[0].name !== null);
  const [ruleOpen, setRuleOpen] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  const toggle = (id: string) =>
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  const doneOf = (requirements: TAnalyticsMyRequirementBE[]) => requirements.filter(r => r.met).length;

  /**
   * A sub-lesson: a quiet heading and its rows, with no disclosure of its own.
   * A third collapse level costs more chrome than the two or three rows it
   * would hide, and members reading a checklist should not have to open a
   * section to find out whether anything is left in it. The rail on the left is
   * what carries the nesting instead.
   */
  const renderSubLesson = (lesson: LessonGroup, key: string) => {
    const all = lessonRequirements(lesson);
    return (
      <Box key={key}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            // Lines up with the rows below it: their List indent plus the row's
            // own horizontal padding.
            pl: { xs: 4, md: 5 },
            pr: { xs: 1.5, md: 2 },
            pt: 1.25,
            pb: 0.25,
          }}
        >
          <Typography
            component="h5"
            sx={{
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 600,
              fontSize: 11.5,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: theme => theme.palette.grey[600],
            }}
          >
            <SectionLabel name={lesson.name} href={lesson.href} />
          </Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontSize: 11.5, color: theme => theme.palette.grey[500] }}>
              {doneOf(all)} of {all.length}
            </Typography>
            {/* Stands in for the chevron every other level has, so this count
                lines up with the ones above and below instead of overhanging. */}
            <Box sx={{ width: 16, flexShrink: 0 }} aria-hidden />
          </Box>
        </Box>
        <List disablePadding sx={{ pl: { xs: 2, md: 3 } }}>
          {lesson.requirements.map(req => (
            <RequirementRow key={req.key} requirement={req} />
          ))}
        </List>
      </Box>
    );
  };

  /** A lesson: collapsible, with its sub-lessons nested inside. */
  const renderLesson = (lesson: LessonGroup, key: string) => {
    const all = lessonRequirements(lesson);
    const open = !collapsed.has(key);
    return (
      <Box key={key}>
        <ButtonBase
          onClick={() => toggle(key)}
          aria-expanded={open}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            width: '100%',
            textAlign: 'left',
            pl: { xs: 2.5, md: 3 },
            pr: { xs: 1.5, md: 2 },
            py: 0.75,
            bgcolor: theme => theme.palette.grey[50],
            color: theme => theme.palette.grey[800],
            borderTop: theme => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            component="h4"
            sx={{ fontFamily: '"Figtree", sans-serif', fontWeight: 700, fontSize: 13, color: 'inherit' }}
          >
            <SectionLabel name={lesson.name} href={lesson.href} />
          </Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontSize: 12, color: 'inherit' }}>
              {doneOf(all)} of {all.length}
            </Typography>
            <ExpandChevron open={open} size={16} />
          </Box>
        </ButtonBase>
        <Collapse in={open} unmountOnExit={false}>
          <List disablePadding sx={{ pl: { xs: 0.5, md: 1 } }}>
            {lesson.requirements.map(req => (
              <RequirementRow key={req.key} requirement={req} />
            ))}
          </List>
          {lesson.subLessons.map(sub => renderSubLesson(sub, `${key}:${sub.id}`))}
        </Collapse>
      </Box>
    );
  };

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <ButtonBase
        onClick={() => setRuleOpen(open => !open)}
        aria-expanded={ruleOpen}
        sx={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          px: { xs: 1.5, md: 2 },
          pt: 2,
          pb: 1.5,
          fontFamily: 'inherit',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: '"Fraunces", serif',
              fontWeight: 500,
              fontSize: 20,
              lineHeight: '28px',
              color: '#2e2e2d',
            }}
          >
            {rule.name}
          </Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: theme => theme.palette.grey[600] }}>
            <Typography variant="body2" sx={{ color: 'inherit' }}>
              {rule.completed} of {rule.total} completed
            </Typography>
            <ExpandChevron open={ruleOpen} />
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: theme => theme.palette.grey[600], mb: 1.5 }}>
          {rule.operator === 'AND' ? 'Complete all of the following' : 'Complete any of the following'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontFamily: '"Fraunces", serif', fontSize: 16.6, lineHeight: '24px', color: '#2e2e2d' }}>
            {rule.percent}%
          </Typography>
          <LinearProgress
            variant="determinate"
            value={rule.percent}
            sx={{
              width: '100%',
              height: 10,
              borderRadius: 3,
              bgcolor: 'rgba(0, 0, 0, 0.1)',
              '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: rule.passed ? '#2e7d32' : '#c45569' },
            }}
          />
        </Box>
      </ButtonBase>

      <Collapse in={ruleOpen} unmountOnExit={false}>
        {groups.map(group => {
          const all = moduleRequirements(group);
          const groupOpen = !collapsed.has(group.id);
          return (
            <Box key={group.id}>
              {showGroupHeaders && (
                <ButtonBase
                  onClick={() => toggle(group.id)}
                  aria-expanded={groupOpen}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    width: '100%',
                    textAlign: 'left',
                    px: { xs: 1.5, md: 2 },
                    py: 1,
                    bgcolor: theme => theme.palette.grey[100],
                    color: theme => theme.palette.grey[700],
                  }}
                >
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: '"Figtree", sans-serif',
                      fontWeight: 700,
                      fontSize: 13,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'inherit',
                    }}
                  >
                    {group.name ? <SectionLabel name={group.name} href={group.href} /> : 'Program'}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: 12, color: 'inherit' }}>
                      {doneOf(all)} of {all.length}
                    </Typography>
                    <ExpandChevron open={groupOpen} size={18} />
                  </Box>
                </ButtonBase>
              )}
              <Collapse in={groupOpen} unmountOnExit={false}>
                <List disablePadding>
                  {group.requirements.map(req => (
                    <RequirementRow key={req.key} requirement={req} />
                  ))}
                </List>
                {group.lessons.map(lesson => renderLesson(lesson, `${group.id}:${lesson.id}`))}
              </Collapse>
            </Box>
          );
        })}
      </Collapse>
    </Card>
  );
};
